"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ArrowRight, Compass, LineChart, GraduationCap, CircleCheck as CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP);
interface EngagementOption {
  title: string;
  badge: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  description: string;
  points: string[];
}
const engagementOptions: EngagementOption[] = [{
  title: "International SEO Audit and Strategy",
  badge: "One-Off Engagement",
  icon: Compass,
  description: "A comprehensive technical audit and market roadmap evaluating your current international footprint, architecture choices, hreflang tags, and priority target countries.",
  points: ["In-depth technical crawl and hreflang conflict analysis", "Market selection and international search demand review", "Keyword research and intent mapping for priority territories", "Clear technical architecture and content localisation roadmap"]
}, {
  title: "Ongoing International SEO Consulting",
  badge: "Retainer Advisory",
  icon: LineChart,
  description: "Continuous strategic guidance, market prioritisation, and iterative optimisation to help your brand systematically build organic visibility across foreign markets.",
  points: ["Regular technical monitoring and release validation", "Phased expansion into additional country and language versions", "Segmented Search Console and analytics performance reviews", "On-demand strategic advice as internal priorities evolve"]
}, {
  title: "Implementation and Training Support",
  badge: "Team Enablement",
  icon: GraduationCap,
  description: "Hands-on guidance and structured workshops for in-house marketing, content, and development teams so they become self-sufficient in international SEO best practice.",
  points: ["SEO briefs and localisation playbooks for content creators", "Technical implementation guidance for web developers", "Hreflang and sitemap maintenance workflows", "Upskilling teams to avoid cross-market keyword cannibalisation"]
}];
export default function Globalexpansioncta() {
  const container = useRef<HTMLDivElement>(null);
  const headlineWords = [{
    text: "Ways",
    italic: false,
    marker: false
  }, {
    text: "to",
    italic: false,
    marker: false
  }, {
    text: "Work",
    italic: true,
    marker: false
  }, {
    text: "Together",
    italic: true,
    marker: true
  }];
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".journey-kicker", {
        y: 12
      });
      gsap.set(".journey-word", {
        y: 38
      });
      gsap.set(".journey-cards", {
        y: 20
      });
      gsap.set(".journey-scope-note", {
        y: 12
      });
      gsap.set(".journey-cta", {
        y: 8
      });
      gsap.set(".journey-alt", {
        y: 8
      });
      gsap.set(".marker-path", {
        strokeDasharray: 300,
        strokeDashoffset: 300
      });
      const tl = gsap.timeline();
      tl.to(".journey-kicker", {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: "power2.out",
        delay: 0.1
      }).to(".journey-word", {
        opacity: 1,
        y: 0,
        duration: 0.72,
        stagger: 0.055,
        ease: "power3.out"
      }, "-=0.2").to(".marker-path", {
        strokeDashoffset: 0,
        duration: 0.7,
        ease: "power2.out"
      }, "+=0.2").to(".journey-cards", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power3.out"
      }, "-=0.2").to(".journey-scope-note", {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: "power2.out"
      }, "-=0.3").to(".journey-cta", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power3.out"
      }, "-=0.3").to(".journey-alt", {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: "power2.out"
      }, "-=0.3");
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".journey-kicker", ".journey-word", ".journey-cards", ".journey-scope-note", ".journey-cta", ".journey-alt"], {
        opacity: 1,
        y: 0
      });
      gsap.set(".marker-path", {
        strokeDasharray: "none",
        strokeDashoffset: 0
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="30571" id="globalexpansioncta" data-nav="light" className="bg-background text-foreground py-20 md:py-32 border-t border-border">
      <div ref={container} className="mx-auto max-w-[1240px] px-4 sm:px-6 md:px-12 text-center">
        <div className="flex flex-col items-center gap-10 md:gap-14">
          {/* Eyebrow */}
          <p className="journey-kicker font-default text-[15px] tracking-[0.02em]" style={{
          opacity: 0,
          color: "var(--muted-foreground)"
        }}>
            {"\u2014 Flexible Engagement Formats"}
          </p>

          {/* H2 Heading */}
          <h2 className="font-serif text-foreground" style={{
          fontSize: "clamp(34px, 4.2vw, 54px)",
          fontWeight: 400,
          letterSpacing: "-0.035em",
          lineHeight: 1.05
        }}>
            <span className="inline-flex flex-wrap justify-center gap-x-[0.25em] gap-y-2">
              {headlineWords.map((word, i) => <span key={i} data-index={i} className="relative inline-block overflow-visible" style={{
              paddingBottom: word.marker ? "0.12em" : "0"
            }}>
                  <span className="journey-word inline-block" style={{
                opacity: 0,
                fontStyle: word.italic ? "italic" : "normal"
              }}>
                    {word.text}
                  </span>
                  {word.marker && <svg aria-hidden="true" viewBox="0 0 300 24" preserveAspectRatio="none" className="pointer-events-none absolute left-0 right-0" style={{
                bottom: "0.02em",
                width: "100%",
                height: "0.18em",
                overflow: "visible"
              }}>
                      <path className="marker-path" d="M4 14 C 40 6, 90 20, 140 12 S 240 6, 296 14" fill="none" stroke="var(--secondary)" strokeWidth="10" strokeLinecap="round" style={{
                  opacity: 0.85
                }} />
                    </svg>}
                </span>)}
            </span>
          </h2>

          {/* 3 Engagement Cards (H3s) */}
          <div className="journey-cards grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 w-full text-left" style={{
          opacity: 0
        }}>
            {engagementOptions.map((option, i) => {
            const Icon = option.icon;
            return <Card key={option.title} data-index={i} className="h-full flex flex-col justify-between border-border bg-card shadow-xs hover:border-primary/40 hover:shadow-md transition-all duration-300">
                  <div>
                    <CardHeader className="pb-4 space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="p-2.5 rounded-lg bg-secondary text-primary">
                          <Icon className="size-5" />
                        </div>
                        <Badge variant="secondary" className="text-xs font-sans">
                          {option.badge}
                        </Badge>
                      </div>
                      <h3 className="text-xl sm:text-2xl font-serif font-semibold text-foreground pt-1">
                        {option.title}
                      </h3>
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed pt-1">
                        {option.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-2">
                      <ul className="space-y-2.5">
                        {option.points.map((pt, pIdx) => <li key={pIdx} className="flex items-start gap-2.5">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" strokeWidth={2} />
                            <span className="text-xs sm:text-sm text-foreground/90 leading-relaxed">
                              {pt}
                            </span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </div>
                </Card>;
          })}
          </div>

          {/* Scope statement */}
          <p className="journey-scope-note max-w-2xl text-sm sm:text-base text-muted-foreground leading-relaxed" style={{
          opacity: 0
        }}>
            Fees depend on the number of target countries, languages, and site complexity. Every engagement is scoped clearly around your specific market expansion goals, and many clients choose to start with an audit before rolling out multi-market work.
          </p>

          {/* Final CTA Buttons */}
          <div className="journey-cta w-full flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4" style={{
          opacity: 0
        }}>
            <Button asChild className="h-auto rounded-lg bg-primary px-8 py-4 md:px-10 md:py-5 text-[17px] font-medium tracking-[0.02em] text-primary-foreground shadow-md transition-all duration-200 hover:bg-primary/90 w-full max-w-[320px] sm:w-auto sm:max-w-none">
              <Link to="/ContactPage">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="ml-2 size-4" strokeWidth={1.5} />
              </Link>
            </Button>

            <Button asChild variant="outline" className="group h-auto rounded-lg border-[1.5px] border-border bg-card px-8 py-4 md:px-8 md:py-5 text-[17px] font-medium tracking-[0.02em] text-foreground transition-all duration-200 hover:border-primary/40 hover:bg-card hover:text-primary w-full max-w-[320px] sm:w-auto sm:max-w-none">
              <Link to="/CaseStudiesPage">
                <span>Explore My Case Studies</span>
                <ArrowRight className="ml-2 size-4 opacity-60 transition-transform duration-200 group-hover:translate-x-1 group-hover:opacity-100" strokeWidth={1.5} />
              </Link>
            </Button>
          </div>

          {/* Alt email */}
          <p className="journey-alt font-default text-[15px] mt-2" style={{
          opacity: 0,
          color: "var(--muted-foreground)"
        }}>
            {"or email "}
            <a href="mailto:contact@ye-consultancy.com" className="text-foreground underline-offset-4 transition-colors duration-200 hover:text-primary hover:underline">
              contact@ye-consultancy.com
            </a>
          </p>
        </div>
      </div>
    </section>;
}